import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';

const TRACK_TYPES = [
  'original',
  'remix',
  'edit',
  'remaster'
];

const ARTIST_ROLES = [
  'main',
  'featured',
  'remixer',
  'vocalist',
  'producer'
];

const TrackMetadata = ({ 
  tracks, 
  setTracks,
  trackMetadata, 
  setTrackMetadata, 
  getTracksRootProps, 
  getTracksInputProps, 
  releaseType 
}) => {
  const [expandedTrack, setExpandedTrack] = useState(null);

  const handleTrackNameChange = (trackIndex, newTitle) => {
    setTracks(prev => prev.map((track, idx) => 
      idx === trackIndex 
        ? { ...track, title: newTitle }
        : track
    ));
  };

  const removeTrack = (trackIndex) => {
    const trackToRemove = tracks[trackIndex];
    
    // Remove from tracks array
    setTracks(prev => prev.filter((_, idx) => idx !== trackIndex));
    
    // Remove from metadata
    setTrackMetadata(prev => {
      const newMetadata = { ...prev };
      delete newMetadata[trackToRemove.file.name];
      return newMetadata;
    });
  };

  const handleMetadataChange = (trackId, field, value) => {
    setTrackMetadata(prev => ({
      ...prev,
      [trackId]: {
        ...prev[trackId],
        [field]: value
      }
    }));
  };

  const handleComposerChange = (trackId, composerIndex, field, value) => {
    const updatedMetadata = { ...trackMetadata };
    updatedMetadata[trackId].composers[composerIndex][field] = value;
    setTrackMetadata(updatedMetadata);
  };

  const addComposer = (trackId) => {
    const updatedMetadata = { ...trackMetadata };
    updatedMetadata[trackId].composers.push({
      firstName: '',
      lastName: '',
      legalName: '',
      share: 0
    });
    setTrackMetadata(updatedMetadata);
  };

  const removeComposer = (trackId, index) => {
    const updatedMetadata = { ...trackMetadata };
    updatedMetadata[trackId].composers.splice(index, 1);
    setTrackMetadata(updatedMetadata);
  };

  const handleArtistChange = (trackId, artistIndex, field, value) => {
    const updatedMetadata = { ...trackMetadata };
    if (!updatedMetadata[trackId].featuredArtists) {
      updatedMetadata[trackId].featuredArtists = [{
        name: '',
        role: 'main'
      }];
    }
    updatedMetadata[trackId].featuredArtists[artistIndex][field] = value;
    setTrackMetadata(updatedMetadata);
  };

  const addArtist = (trackId) => {
    const updatedMetadata = { ...trackMetadata };
    if (!updatedMetadata[trackId].featuredArtists) {
      updatedMetadata[trackId].featuredArtists = [{
        name: '',
        role: 'main'
      }];
    }
    updatedMetadata[trackId].featuredArtists.push({
      name: '',
      role: 'featured'
    });
    setTrackMetadata(updatedMetadata);
  };

  const removeArtist = (trackId, index) => {
    const updatedMetadata = { ...trackMetadata };
    const artists = updatedMetadata[trackId].featuredArtists;
    
    // Don't allow removing if it's the only main artist
    const mainArtists = artists.filter(a => a.role === 'main');
    if (artists[index].role === 'main' && mainArtists.length === 1) {
      return; // Don't remove the last main artist
    }
    
    updatedMetadata[trackId].featuredArtists.splice(index, 1);
    setTrackMetadata(updatedMetadata);
  };

  return (
    <Box>
      {/* File Upload Section */}
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Box {...getTracksRootProps()} sx={{ 
          border: '2px dashed',
          borderColor: 'primary.main',
          borderRadius: 2,
          p: 4,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}>
          <input {...getTracksInputProps()} />
          <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Upload Audio Files
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drag and drop your audio files here, or click to browse
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Supported formats: MP3, WAV, FLAC, AAC, OGG
          </Typography>
        </Box>
      </Paper>

      {/* Track Count Info */}
      {tracks.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {tracks.length} track{tracks.length !== 1 ? 's' : ''} uploaded for {releaseType}
        </Alert>
      )}

      {/* Track List with Metadata */}
      {tracks.map((track, index) => (
        <Accordion
          key={track.file.name}
          expanded={expandedTrack === track.file.name}
          onChange={() => setExpandedTrack(expandedTrack === track.file.name ? null : track.file.name)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography sx={{ flexGrow: 1 }}>
                {`${index + 1}. ${track.title}`}
              </Typography>
              <IconButton 
                onClick={(e) => {
                  e.stopPropagation();
                  removeTrack(index);
                }}
                color="error"
                size="small"
                sx={{ ml: 2 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Track Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Track Name"
                  value={track.title}
                  onChange={(e) => handleTrackNameChange(index, e.target.value)}
                  helperText="The display name for this track"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}
                />
              </Grid>

              {/* Track Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#666666' },
                    '&:hover fieldset': { borderColor: '#666666' },
                    '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                  }
                }}>
                  <InputLabel>Track Type</InputLabel>
                  <Select
                    value={trackMetadata[track.file.name]?.trackType || 'original'}
                    onChange={(e) => handleMetadataChange(track.file.name, 'trackType', e.target.value)}
                    label="Track Type"
                  >
                    {TRACK_TYPES.map(type => (
                      <MenuItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* ISRC */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ISRC"
                  value={trackMetadata[track.file.name]?.isrc || ''}
                  onChange={(e) => handleMetadataChange(track.file.name, 'isrc', e.target.value)}
                  helperText="Optional. Will be auto-generated if not provided"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}
                />
              </Grid>

              {/* Artists Section */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Artists
                  </Typography>
                  {trackMetadata[track.file.name]?.featuredArtists?.map((artist, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Artist Name"
                            value={artist.name || ''}
                            onChange={(e) => handleArtistChange(track.file.name, idx, 'name', e.target.value)}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#666666' },
                                '&:hover fieldset': { borderColor: '#666666' },
                                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#666666' },
                              '&:hover fieldset': { borderColor: '#666666' },
                              '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                            }
                          }}>
                            <InputLabel>Role</InputLabel>
                            <Select
                              value={artist.role}
                              onChange={(e) => handleArtistChange(track.file.name, idx, 'role', e.target.value)}
                              label="Role"
                            >
                              {ARTIST_ROLES.map(role => (
                                <MenuItem 
                                  key={role} 
                                  value={role}
                                >
                                  {role.charAt(0).toUpperCase() + role.slice(1)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <IconButton 
                            color="error" 
                            onClick={() => removeArtist(track.file.name, idx)}
                            disabled={
                              artist.role === 'main' && 
                              trackMetadata[track.file.name]?.featuredArtists?.filter(a => a.role === 'main').length === 1
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                          {artist.role === 'main' && 
                           trackMetadata[track.file.name]?.featuredArtists?.filter(a => a.role === 'main').length === 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              At least one main artist is required
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addArtist(track.file.name)}
                  >
                    Add Artist
                  </Button>
                </Box>
              </Grid>

              {/* Optional Composers Section */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Composers (Optional)
                  </Typography>
                  {trackMetadata[track.file.name]?.composers?.map((composer, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="First Name"
                            value={composer.firstName}
                            onChange={(e) => handleComposerChange(track.file.name, idx, 'firstName', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#666666' },
                                '&:hover fieldset': { borderColor: '#666666' },
                                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Last Name"
                            value={composer.lastName}
                            onChange={(e) => handleComposerChange(track.file.name, idx, 'lastName', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#666666' },
                                '&:hover fieldset': { borderColor: '#666666' },
                                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Legal Name"
                            value={composer.legalName}
                            onChange={(e) => handleComposerChange(track.file.name, idx, 'legalName', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#666666' },
                                '&:hover fieldset': { borderColor: '#666666' },
                                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <IconButton 
                            color="error" 
                            onClick={() => removeComposer(track.file.name, idx)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addComposer(track.file.name)}
                  >
                    Add Composer
                  </Button>
                </Box>
              </Grid>

              {/* Publishing Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Publishing Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={trackMetadata[track.file.name]?.publishing.isCopyrightControl}
                          onChange={(e) => handleMetadataChange(
                            track.file.name,
                            'publishing',
                            {
                              ...trackMetadata[track.file.name].publishing,
                              isCopyrightControl: e.target.checked
                            }
                          )}
                        />
                      }
                      label="Copyright Control"
                    />
                  </Grid>
                  {!trackMetadata[track.file.name]?.publishing.isCopyrightControl && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Publishing House"
                          value={trackMetadata[track.file.name]?.publishing.house || ''}
                          onChange={(e) => handleMetadataChange(
                            track.file.name,
                            'publishing',
                            {
                              ...trackMetadata[track.file.name].publishing,
                              house: e.target.value
                            }
                          )}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#666666' },
                              '&:hover fieldset': { borderColor: '#666666' },
                              '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Publishing Rights Information"
                          value={trackMetadata[track.file.name]?.publishing.rights || ''}
                          onChange={(e) => handleMetadataChange(
                            track.file.name,
                            'publishing',
                            {
                              ...trackMetadata[track.file.name].publishing,
                              rights: e.target.value
                            }
                          )}
                          multiline
                          rows={2}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#666666' },
                              '&:hover fieldset': { borderColor: '#666666' },
                              '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default TrackMetadata;